import { CheckCircle, Circle, EllipsisVertical, Calendar as CalendarIcon, Repeat as RepeatIcon, Users, Paperclip, Trash2, Goal, TimerIcon, ChevronDown, X, PlayCircle, SquareSquare, GripVertical, Star } from "lucide-react";
import React, { useRef, useState } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { format } from "date-fns";
import Repeat from "./Repeat";
import Attachments from "./Attachments";
import Assigne from "./Assigne";

const TaskCards = () => {
    const [tasks, setTasks] = useState([
        {
            id: 1,
            title: "Write project summary",
            details: "Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's standard dummy text ever since the 1500s, when an unknown printer took a galley of type and scrambled it to make a type specimen book. It has survived not only five centuries, but also the leap into electronic typesetting, remaining essentially unchanged. It was popularised in the 1960s with the release of Letraset sheets containing Lorem Ipsum passages, and more recently with desktop publishing software like Aldus PageMaker including versions of Lorem Ipsum."
        },
        {
            id: 2,
            title: "Fix login bug",
            details: "Resolve the authentication issue affecting mobile users."
        },
        {
            id: 3,
            title: "Design dashboard layout",
            details: "Create wireframes for the analytics dashboard UI."
        },
        {
            id: 4,
            title: "Email team updates",
            details: "Send weekly progress update to the team."
        },
        {
            id: 5,
            title: "Prepare client demo",
            details: "Set up demo environment and test all flows."
        },
    ]);

    const textareaRef = useRef(null);
    const [openMenu, setOpenMenu] = useState(null);
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const [dueSelection, setDueSelection] = useState(null);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [selectedTime, setSelectedTime] = useState("");
    const [repeatSettings, setRepeatSettings] = useState(null);
    const [isRepeatModalOpen, setIsRepeatModalOpen] = useState(false);
    const [isAttachmentsOpen, setIsAttachmentsOpen] = useState(false);
    const [attachedFiles, setAttachedFiles] = useState([]);
    const [deadlineDate, setDeadlineDate] = useState(null);
    const [deadlineTime, setDeadlineTime] = useState("");
    const [isDeadlineModalOpen, setIsDeadlineModalOpen] = useState(false);
    const [assignedUsers, setAssignedUsers] = useState([]);
    const [isAssigneeModalOpen, setIsAssigneeModalOpen] = useState(false);
    const [isRunning, setIsRunning] = useState(false);
    const [isStopReasonModalOpen, setIsStopReasonModalOpen] = useState(false);
    const [stopReason, setStopReason] = useState("");
    const [customStopReason, setCustomStopReason] = useState("");
    const [showCustomStopInput, setShowCustomStopInput] = useState(false);
    const [taskStartTime, setTaskStartTime] = useState(null);
    const [isCompleting, setIsCompleting] = useState(false);
    const [isStarred, setIsStarred] = useState(false);
    const handleInput = () => {
        const textarea = textareaRef.current;
        if (!textarea) return;
        textarea.style.height = "auto";
        const scrollH = textarea.scrollHeight;
        const maxHeight = 10 * 24;
        textarea.style.height = scrollH < maxHeight ? scrollH + "px" : maxHeight + "px";
        textarea.style.overflowY = scrollH < maxHeight ? "hidden" : "auto";
    };


    const handleStarClick = () => {
        setIsStarred(prev => !prev);
    };


    const handleComplete = () => {
        setIsCompleting(true);

        setTimeout(() => {
            setIsCompleting(false);
        }, 1000);
    };

    const handleStartStopClick = () => {
        if (isRunning) {
            // Was running → now show stop reason modal
            setIsStopReasonModalOpen(true);
        } else {
            // Was stopped → start immediately
            setIsRunning(true);
            setTaskStartTime(new Date());
            // Optional: default reason or log start
            console.log("Timer started at:", new Date());
        }
    };

    const handleToday = () => {
        setDueSelection("today");
        setSelectedDate(new Date());
        setSelectedTime("");
        setIsCalendarOpen(false);
    };

    const handleTomorrow = () => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        setDueSelection("tomorrow");
        setSelectedDate(tomorrow);
        setSelectedTime("");
        setIsCalendarOpen(false);
    };

    const openCalendar = () => {
        setIsCalendarOpen(true);
    };

    const clearDueDate = (e) => {
        e.stopPropagation();
        setDueSelection(null);
        setSelectedTime("");
    };

    const applyCustomDate = () => {
        setDueSelection({ date: selectedDate, time: selectedTime || undefined });
        setIsCalendarOpen(false);
    };

    const openDeadlineModal = () => {
        setIsDeadlineModalOpen(true);
        setOpenMenu(null); // Close menu
    };

    const applyDeadline = () => {
        setDeadlineDate(selectedDate);
        setIsDeadlineModalOpen(false);
    };

    const clearDeadline = () => {
        setDeadlineDate(null);
        setDeadlineTime("");
    };

    const openAssigneeModal = () => {
        setIsAssigneeModalOpen(true);
        setOpenMenu(null);
    };

    const getDeadlineDisplay = () => {
        if (!deadlineDate) return "Deadline";
        const dateStr = format(deadlineDate, "MMM d, yyyy");
        return deadlineTime ? `${dateStr} at ${formatTime(deadlineTime)}` : dateStr;
    };

    const getDisplayText = () => {
        if (!dueSelection) return null;

        if (dueSelection === "today") {
            return selectedTime ? `Today, ${formatTime(selectedTime)}` : "Today";
        }
        if (dueSelection === "tomorrow") {
            return selectedTime ? `Tomorrow, ${formatTime(selectedTime)}` : "Tomorrow";
        }
        if (dueSelection?.date) {
            const dateStr = format(dueSelection.date, "MMM d");
            return selectedTime ? `${dateStr}, ${formatTime(selectedTime)}` : dateStr;
        }
        return null;
    };

    const formatTime = (time24) => {
        if (!time24) return "";
        const [h, m] = time24.split(":");
        const hour = parseInt(h);
        const period = hour >= 12 ? "PM" : "AM";
        const displayHour = hour % 12 || 12;
        return `${displayHour}:${m} ${period}`;
    };

    const displayText = getDisplayText();

    const getMinDeadlineDate = () => {
        if (!repeatSettings?.startsOn) return new Date();
        const start = new Date(repeatSettings.startsOn);
        const min = new Date(start);
        min.setDate(start.getDate() + 1); // Next day after start
        return min;
    };

    // Drag & Drop handlers
    const onDragStart = (e, index) => {
        e.dataTransfer.setData("taskIndex", index);
        e.currentTarget.classList.add("opacity-50");
    };

    const onDragOver = (e) => {
        e.preventDefault(); // Allow drop
        e.currentTarget.classList.add("bg-gray-50");
    };

    const onDragLeave = (e) => {
        e.currentTarget.classList.remove("bg-gray-50");
    };

    const onDrop = (e, dropIndex) => {
        e.preventDefault();
        const dragIndex = Number(e.dataTransfer.getData("taskIndex"));

        const newTasks = [...tasks];
        const [draggedTask] = newTasks.splice(dragIndex, 1);
        newTasks.splice(dropIndex, 0, draggedTask);

        setTasks(newTasks);

        // Clean up styles
        e.currentTarget.classList.remove("bg-gray-50");
        document.querySelectorAll(".dragging").forEach(el => {
            el.classList.remove("opacity-50");
        });
    };

    const onDragEnd = (e) => {
        e.currentTarget.classList.remove("opacity-50");
    };

    return (
        <>
            <div className="flex items-start gap-4 overflow-x-auto whitespace-nowrap [&::-webkit-scrollbar]:h-1 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-blue-500">
                <div className="border border-line rounded-lg p-3 min-w-80 w-full bg-surface task-card-height overflow-y-auto [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-primary">
                    <div className="flex justify-between items-center gap-2 pb-2 border-b border-line">
                        <h2 className="text-base font-semibold tracking-wider text-ink">Vishal</h2>
                        <span className="flex justify-center items-center rounded text-ink cursor-pointer">
                            <EllipsisVertical size={18} />
                        </span>
                    </div>

                    <div className="space-y-3">
                        <div className="flex items-center gap-2 cursor-pointer pt-2 font-semibold text-base tracking-wide">
                            <CheckCircle size={20} /> Add task
                        </div>

                        {tasks.map((task, index) => (
                            <div
                                key={task.id}
                                draggable
                                onDragStart={(e) => onDragStart(e, index)}
                                onDragOver={onDragOver}
                                onDragLeave={onDragLeave}
                                onDrop={(e) => onDrop(e, index)}
                                onDragEnd={onDragEnd}
                                className="flex items-start gap-2 w-full group"
                            >
                                <div className="flex flex-col gap-3">
                                    <div
                                        className="flex justify-center items-center shrink-0 mt-1 cursor-pointer"
                                        onClick={handleComplete}
                                    >
                                        {isCompleting ? (
                                            <CheckCircle
                                                size={18}
                                                className="text-green-500 scale-110 transition-transform duration-200"
                                            />
                                        ) : (
                                            <Circle size={18} className="text-gray-500 hover:text-green-600" />
                                        )}
                                    </div>
                                    <div className="mt-1 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity">
                                        <GripVertical size={18} className="text-gray-400" />
                                    </div>

                                </div>

                                <div className="w-full relative">
                                    <div className="flex justify-between gap-2 items-center relative">
                                        <input
                                            type="text"
                                            value={task.title}
                                            onChange={(e) => {
                                                const newTasks = [...tasks];
                                                newTasks[index].title = e.target.value;
                                                setTasks(newTasks);
                                            }}
                                            className="text-sm font-semibold border-0 outline-0 w-full text-black"
                                            placeholder="Title"
                                        />
                                        <div className="flex justify-end items-end gap-2">
                                            <span
                                                onClick={() => setOpenMenu(openMenu === "title" ? null : "title")}
                                                className="cursor-pointer opacity-0 group-hover:opacity-100"
                                            >
                                                <EllipsisVertical size={18} />
                                            </span>
                                            <span
                                                onClick={handleStarClick}
                                                className={`
                cursor-pointer transition opacity-0 group-hover:opacity-100
                ${isStarred ? "text-yellow-400" : "text-gray-400"}
            `}
                                            >
                                                <Star
                                                    size={18}
                                                    className={`
                    transition-all duration-200 
                    ${isStarred ? "fill-yellow-400 scale-100" : "scale-100"}
                `}
                                                />
                                            </span>
                                        </div>
                                    </div>
                                    {openMenu === "title" && (
                                        <div className="absolute right-0 top-8 w-40 bg-white shadow-md rounded-md border z-10">
                                            <button
                                                onClick={handleStartStopClick}
                                                className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 w-full text-left text-sm"
                                            >
                                                {isRunning ? <SquareSquare size={14} /> : <PlayCircle size={14} />}
                                                <span>{isRunning ? "Stop" : "Start"}</span>
                                            </button>
                                            <button
                                                onClick={openDeadlineModal}
                                                className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 w-full text-left text-sm"
                                            >
                                                <Goal size={14} className={deadlineDate ? "text-blue-600" : ""} />
                                                <span className={deadlineDate ? "text-blue-600" : ""}>
                                                    {getDeadlineDisplay()}
                                                </span>
                                            </button>
                                            <button
                                                onClick={openAssigneeModal}
                                                className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 w-full text-left"
                                            >
                                                <Users size={14} className={assignedUsers.length > 0 ? "text-blue-600" : ""} />
                                                <span className={`text-sm ${assignedUsers.length > 0 ? "text-blue-600" : ""}`}>
                                                    {assignedUsers.length > 0 ? `${assignedUsers.length} assignee${assignedUsers.length > 1 ? "s" : ""}` : "Assignee"}
                                                </span>
                                            </button>
                                            <button className="flex items-center gap-2 px-2 py-1 hover:bg-gray-100 w-full text-left text-sm text-red-600">
                                                <Trash2 size={14} /> Delete
                                            </button>
                                            <div className="border-t my-1" />
                                            <div className="px-2 py-1 flex items-center gap-2 text-sm">
                                                <CheckCircle size={14} className="text-blue-600" /> Selected List
                                            </div>
                                            <div className="px-2 py-1 text-sm pl-[30px]">Another List</div>
                                        </div>
                                    )}

                                    {/* BEST VERSION - Replace your current textarea block with this */}
                                    <div className="mt-1">
                                        <textarea
                                            value={task.details}
                                            onChange={(e) => {
                                                const newTasks = [...tasks];
                                                newTasks[index].details = e.target.value;
                                                setTasks(newTasks);
                                            }}
                                            placeholder="Details"
                                            rows={1}
                                            className={`
      w-full text-sm text-gray-700 bg-transparent border-0 outline-none resize-none
      overflow-hidden transition-all duration-300
      ${task.details ? "line-clamp-2 " : ""}
      focus:line-clamp-none 
    `}
                                            style={{
                                                minHeight: "1.4rem",
                                                lineHeight: "1.4rem",
                                                height: task.details ? "2.8rem" : "1.4rem", // 2 lines when not focused
                                            }}
                                            onFocus={(e) => {
                                                e.target.style.height = "auto";
                                                e.target.style.height = Math.max(e.target.scrollHeight, 80) + "px";
                                            }}
                                            onInput={(e) => {
                                                e.target.style.height = "auto";
                                                e.target.style.height = Math.max(e.target.scrollHeight, 80) + "px";
                                            }}
                                            onBlur={(e) => {
                                                if (!e.target.value.trim()) {
                                                    e.target.style.height = "1.4rem";
                                                } else {
                                                    e.target.style.height = "2.8rem"; // back to 2 lines
                                                }
                                            }}
                                        />
                                    </div>

                                    {/* DUE DATE OPTIONS */}
                                    <div className="flex flex-wrap items-center gap-2 mt-1">
                                        {/* Show selected pill when something is selected */}
                                        {displayText ? (
                                            <div className="flex items-center justify-between gap-1 bg-blue-50 text-blue-700 border border-blue-700 px-1.5 py-0.5 rounded-md text-xs font-medium">
                                                <div className="flex items-center justify-start gap-1">
                                                    <CalendarIcon size={14} />
                                                    <span className="cursor-pointer" onClick={openCalendar}>{displayText}</span>
                                                </div>
                                                <button onClick={clearDueDate} className="hover:bg-blue-700 hover:text-white rounded-full p-0.5">
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        ) : (
                                            <>
                                                <button
                                                    onClick={handleToday}
                                                    className="px-2 py-1 text-xs rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition"
                                                >
                                                    Today
                                                </button>

                                                <button
                                                    onClick={handleTomorrow}
                                                    className="px-2 py-1 text-xs rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition"
                                                >
                                                    Tomorrow
                                                </button>

                                                <button
                                                    onClick={openCalendar}
                                                    className="p-1 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition"
                                                >
                                                    <CalendarIcon size={16} />
                                                </button>
                                            </>
                                        )}
                                    </div>

                                    {/* RIGHT ICONS */}
                                    <div className="flex gap-2 mt-2">
                                        <div className="flex items-center gap-2">

                                            {/* SHOW BUTTON ONLY WHEN NO FILES ATTACHED */}
                                            {attachedFiles.length === 0 && (
                                                <button
                                                    onClick={() => setIsAttachmentsOpen(true)}
                                                    className="p-1 border rounded-md hover:bg-gray-50 border-gray-300 transition"
                                                >
                                                    <Paperclip size={16} />
                                                </button>
                                            )}

                                            {/* SHOW TEXT ONLY WHEN FILES ATTACHED */}
                                            {attachedFiles.length > 0 && (
                                                <p
                                                    onClick={() => setIsAttachmentsOpen(true)}
                                                    className="flex items-center gap-1 bg-blue-50 text-blue-700 border border-blue-700 px-1.5 py-1 rounded-md text-xs font-medium cursor-pointer"
                                                >
                                                    <span><Paperclip size={16} /></span>
                                                    <span>
                                                        {attachedFiles.length === 1
                                                            ? "1 file attached"
                                                            : `${attachedFiles.length} files attached`}
                                                    </span>
                                                </p>
                                            )}

                                        </div>

                                        <button
                                            onClick={() => setIsRepeatModalOpen(true)}
                                            className={`p-1 border rounded-md transition ${repeatSettings ? "bg-blue-50 border-blue-700 text-blue-700" : "hover:bg-gray-50"}`}
                                        >
                                            <RepeatIcon size={16} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Calendar Modal */}
            {isCalendarOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setIsCalendarOpen(false)}>
                    <div className="bg-white rounded-lg shadow-xl p-3 w-80 max-w-full" onClick={(e) => e.stopPropagation()}>
                        <Calendar
                            onChange={(date) => setSelectedDate(date)}
                            value={selectedDate}
                            minDate={new Date()}
                        />

                        {/* Time Selector */}
                        <div className="flex items-center gap-3 mt-4">
                            <TimerIcon size={18} className="text-gray-600" />
                            <div className="relative flex-1">
                                <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none z-10" />
                                <select
                                    value={selectedTime}
                                    onChange={(e) => setSelectedTime(e.target.value)}
                                    className="w-full border rounded-md px-3 py-2 pr-10 appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Set Time</option>
                                    {Array.from({ length: 24 * 4 }).map((_, i) => {
                                        const hours24 = Math.floor(i / 4);
                                        const minutes = (i % 4) * 15;
                                        const timeStr = `${String(hours24).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
                                        const displayHour = hours24 % 12 || 12;
                                        const period = hours24 < 12 ? "AM" : "PM";
                                        return (
                                            <option key={timeStr} value={timeStr}>
                                                {displayHour}:{String(minutes).padStart(2, "0")} {period}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                        </div>

                        {/* Repeat (optional) */}
                        <div className="flex items-center gap-3 mt-3 cursor-pointer hover:bg-gray-50 -ml-2 px-2 py-1 rounded">
                            <RepeatIcon size={18} className="text-gray-600" />
                            <span
                                onClick={() => setIsRepeatModalOpen(true)}
                                className="text-gray-700">Repeat</span>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-end gap-3 mt-5">
                            <button
                                onClick={() => setIsCalendarOpen(false)}
                                className="px-4 py-2 text-sm rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 transition"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={applyCustomDate}
                                className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition"
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                </div>
            )}


            {/* Deadline Modal */}
            {isDeadlineModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setIsDeadlineModalOpen(false)}>
                    <div className="bg-white rounded-xl shadow-2xl p-3 w-96" onClick={e => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold mb-4">Set Deadline</h3>
                        <Calendar
                            onChange={setSelectedDate}
                            value={deadlineDate || getMinDeadlineDate()}
                            minDate={getMinDeadlineDate()}
                        />
                        <div className="flex justify-end gap-3 mt-6">
                            <button onClick={() => setIsDeadlineModalOpen(false)} className="px-5 py-2 bg-gray-200 rounded-lg">
                                Cancel
                            </button>
                            <button onClick={applyDeadline} className="px-5 py-2 bg-blue-600 text-white rounded-lg">
                                Apply
                            </button>
                        </div>
                    </div>
                </div>
            )}


            {/* Stop Reason Modal - Appears ONLY when clicking Stop */}
            {isStopReasonModalOpen && (
                <div
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
                    onClick={() => setIsStopReasonModalOpen(false)}
                >
                    <div
                        className="bg-white rounded-xl shadow-2xl p-3 w-[480px] max-w-full max-h-[90vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 className="text-base font-semibold">Task completed or paused?</h3>
                        <p className="text-xs text-gray-600 mb-3">Why are you stopping the timer?</p>

                        {/* 2-Column Grid */}
                        <div className="grid grid-cols-2 gap-2">
                            {[
                                "Task completed",
                                "Taking a break",
                                "Switching tasks",
                                "Meeting / Call",
                                "Got blocked",
                                "Waiting for review/feedback",
                                "Lunch / Personal time",
                                "End of workday",
                                "Technical issue",
                                "Other",
                            ].map((reason) => (
                                <button
                                    key={reason}
                                    onClick={() => {
                                        if (reason === "Other") {
                                            setShowCustomStopInput(true);
                                            setStopReason("");
                                        } else {
                                            setStopReason(reason);
                                            setShowCustomStopInput(false);
                                            setCustomStopReason("");
                                        }
                                    }}
                                    className={`text-left px-2 py-2 rounded-lg border text-xs transition ${stopReason === reason ||
                                        (reason === "Other" && showCustomStopInput)
                                        ? "border-blue-600 bg-blue-50 text-blue-700"
                                        : "border-gray-300 hover:bg-gray-50"
                                        }`}
                                >
                                    {reason}
                                </button>
                            ))}
                        </div>

                        {/* Custom Reason Input */}
                        {showCustomStopInput && (
                            <input
                                type="text"
                                value={customStopReason}
                                onChange={(e) => setCustomStopReason(e.target.value)}
                                placeholder="e.g., Waiting for client approval"
                                className="mt-3 w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                autoFocus
                            />
                        )}

                        {/* Action Buttons */}
                        <div className="flex justify-end gap-2 mt-4">
                            <button
                                onClick={() => {
                                    setIsStopReasonModalOpen(false);
                                    setShowCustomStopInput(false);
                                    setCustomStopReason("");
                                    setStopReason("");
                                }}
                                className="px-2 py-1 bg-gray-200 text-sm rounded-lg hover:bg-gray-300"
                            >
                                Cancel
                            </button>

                            <button
                                onClick={() => {
                                    const finalReason =
                                        showCustomStopInput && customStopReason.trim()
                                            ? customStopReason.trim()
                                            : stopReason || "Stopped without reason";

                                    const duration = taskStartTime
                                        ? Math.round((new Date() - taskStartTime) / 1000)
                                        : 0;

                                    console.log("Time tracked:", {
                                        reason: finalReason,
                                        duration_seconds: duration,
                                        startedAt: taskStartTime,
                                        endedAt: new Date(),
                                    });

                                    setIsRunning(false);
                                    setTaskStartTime(null);
                                    setIsStopReasonModalOpen(false);
                                    setStopReason("");
                                    setCustomStopReason("");
                                    setShowCustomStopInput(false);
                                }}
                                disabled={!stopReason && !(showCustomStopInput && customStopReason.trim())}
                                className={`px-2 py-2 rounded-lg text-sm font-medium transition ${stopReason || (showCustomStopInput && customStopReason.trim())
                                    ? "bg-blue-600 hover:bg-blue-700 text-white"
                                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                                    }`}
                            >
                                Stop Timer
                            </button>
                        </div>
                    </div>

                </div>
            )}

            <Repeat
                isOpen={isRepeatModalOpen}
                onClose={() => setIsRepeatModalOpen(false)}
                onApply={(settings) => {
                    setRepeatSettings(settings);
                    console.log("Repeat applied:", settings); // You can save to task here
                }}
            />

            <Attachments
                isOpen={isAttachmentsOpen}
                onClose={() => setIsAttachmentsOpen(false)}
                onApply={(files) => {
                    setAttachedFiles(files);
                    console.log("Attached files:", files);
                }}
                attachedFiles={attachedFiles}
            />

            <Assigne
                isOpen={isAssigneeModalOpen}
                onClose={() => setIsAssigneeModalOpen(false)}
                onApply={(users) => {
                    setAssignedUsers(users);
                }}
            />
        </>
    );
};

export default TaskCards;